import { useState } from 'react'
import { useAssignPrograms, useProgram, useReviewMutation, useUpsertProgram } from '@/api/queries'
import type { ComboboxOption } from '@/components/ui/Combobox'
import { useToast } from '@/components/ui/Toast'
import { FlagToggle } from '@/components/review/FlagToggle'
import { ReviewQueueModal } from '@/components/review/ReviewQueueModal'
import { TagEditor } from '@/components/review/TagEditor'
import { useEntityDraft } from '@/components/review/useEntityDraft'
import type { ReviewQueue } from '@/components/review/useReviewQueue'
import { PreviouslyRejectedFlag, VerdictHistory } from '@/components/review/VerdictHistory'
import type { Program } from '@/types'
import { isValidProgramUrl, ProgramDetails, ProgramPageField, WbProgramAssignField } from './ProgramFields'
import { isGraduate } from './translations'

/** The degree's own edits. Its WB program is assigned at once; segment and slate are the WB program's (PC-33). */
interface Draft {
  programUrl: string
  tags: string[]
}

/**
 * "Review pending" on the programs page (APP-140): the next pending degree,
 * editable, with Accept, Reject and Skip. A verdict saves any edits first,
 * then the verdict, and moves on only when both succeed; a failure keeps the
 * item and the draft on screen. Skip saves nothing.
 */
export function ProgramReviewQueue({
  clientId,
  queue,
  wbOptions,
  tagPool,
  onFlag,
}: {
  clientId: string
  queue: ReviewQueue<Program>
  wbOptions: ComboboxOption[]
  tagPool: string[]
  onFlag: (id: string, flagged: boolean) => void
}) {
  const upsert = useUpsertProgram(clientId)
  const review = useReviewMutation(clientId, 'programs')
  const assign = useAssignPrograms(clientId)
  const toast = useToast()
  const [busy, setBusy] = useState<'approved' | 'rejected' | null>(null)

  const program = queue.current
  const { data: detail } = useProgram(clientId, program?.id ?? null)
  const draft = useEntityDraft<Draft>(
    program
      ? { programUrl: program.programUrl ?? '', tags: program.tags }
      : null,
    { programUrl: '', tags: [] },
  )

  // A new item starts from its own record, not the last one's edits.
  const [shownId, setShownId] = useState(program?.id)
  if (program?.id !== shownId) {
    setShownId(program?.id)
    draft.reset()
  }

  async function decide(status: 'approved' | 'rejected') {
    if (!program) return
    if (!isValidProgramUrl(draft.value.programUrl)) {
      toast.push('Fix the program page URL first.', 'error')
      return
    }
    setBusy(status)
    try {
      if (draft.dirty) {
        await upsert.mutateAsync({ id: program.id, tags: draft.value.tags, programUrl: draft.value.programUrl.trim() || null })
      }
      await review.mutateAsync({ ids: [program.id], status })
    } catch {
      toast.push(`Could not ${status === 'approved' ? 'accept' : 'reject'} the program.`, 'error')
      return
    } finally {
      setBusy(null)
    }
    queue.advance()
  }

  return (
    <ReviewQueueModal
      open={queue.active}
      onClose={queue.stop}
      noun="program"
      title={program?.wbProgram || program?.name || ''}
      subtitle={program?.degree ?? 'No degree stated'}
      remaining={queue.remaining}
      done={queue.done}
      busy={busy}
      onApprove={() => void decide('approved')}
      onReject={() => void decide('rejected')}
      onSkip={queue.advance}
    >
      {program && (
        <>
          {program.previouslyRejected && <PreviouslyRejectedFlag />}

          {/* A graduate program has no WB program (PC-25). */}
          {!isGraduate(program) && (
            <WbProgramAssignField
              value={program.wbProgramId}
              options={wbOptions}
              onAssign={(target) =>
                assign.mutate(
                  { ids: [program.id], target },
                  { onError: () => toast.push('Could not change the WB program.', 'error') },
                )
              }
            />
          )}
          <ProgramPageField value={draft.value.programUrl} onChange={(v) => draft.set('programUrl', v)} />

          <div className="space-y-1.5">
            <span className="field-label block">Tags</span>
            <TagEditor tags={draft.value.tags} suggestions={tagPool} onChange={(tags) => draft.set('tags', tags)} />
          </div>

          <div className="flex items-center gap-2">
            <span className="field-label">Flag</span>
            <FlagToggle flagged={program.flagged} onChange={(next) => onFlag(program.id, next)} />
          </div>

          <ProgramDetails program={program} detail={detail} />

          <div className="border-t border-rule pt-4">
            <VerdictHistory verdicts={program.verdicts} entityHash={program.entityHash} />
          </div>
        </>
      )}
    </ReviewQueueModal>
  )
}
